import React, {useEffect, useState} from 'react';
import {Box, Button, CircularProgress} from '@mui/material';
import PropTypes from 'prop-types';
import {UploadFile} from '@mui/icons-material';
import {downloadFormDocument, useUploadFormDocumentsMutation} from '../../../../../api/services/filesService';
import AdmicityDialog from '../../../../../shared-components/AdmicityDialog';
import Uploader from '../../../../SchoolManagement/Setup/Uploader';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';
import {stripHtmlTags} from './AdditionalNotes';

const Image = ({
                   id,
                   index,
                   onChange,
                   description,
                   isEditMode,
                   attachments,
                   isFormArchived,
                   readonly,
                   placeholderValues,
                   accessLevel,
                   audience,
                   recipientType,
                   submitMode
               }) => {
    const [uploadFiles] = useUploadFormDocumentsMutation();
    const [openAddDocumentsDialog, setOpenAddDocumentsDialog] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isUploadSizeExceedsLimit, setIsUploadSizeExceedsLimit] = useState(false);
    const [previewImageUrl, setPreviewImageUrl] = useState(undefined);

    const [currentValue, setCurrentValue] = useState(description ?? '');

    useEffect(() => {
        setCurrentValue(description ?? '');
    }, [description]);

    useEffect(() => {
        if (attachments && attachments.length) {
            downloadFormDocument({documentId: attachments[0].id}).then(r => {
                setPreviewImageUrl(URL.createObjectURL(r.blob));
            });
        }

        return () => {
            if (previewImageUrl) {
                URL.revokeObjectURL(previewImageUrl);
            }
        };
    }, [attachments]);

    const handleDescriptionChange = (nextValue) => {
        setCurrentValue(nextValue);

        if (!onChange) return;

        onChange({
            index,
            description: stripHtmlTags(nextValue),
            formattedDescription: nextValue
        });
    };

    const handleFilesUpload = async () => {
        setIsUploading(true);
        const uploadedFiles = await uploadFiles(selectedFiles);
        onChange({index, files: [uploadedFiles.data[0]]});
        handleDialogClose();
    };

    const handleDialogClose = () => {
        setOpenAddDocumentsDialog(false);
        setSelectedFiles([]);
        setIsUploading(false);
        setIsUploadSizeExceedsLimit(false);
    };

    const handleFilesSelect = (files) => {
        setSelectedFiles(files);
    };

    const handleFileDeleteFromUploadContext = (filename) => {
        const files = [...selectedFiles];
        setSelectedFiles(files.filter(f => f.name !== filename));
    };

    return (
        <Box sx={{mt: 2}}>
            {isEditMode
                ? (
                    <>
                        <Button
                            component="label"
                            variant="outlined"
                            startIcon={<UploadFile/>}
                            onClick={() => setOpenAddDocumentsDialog(true)}
                            disabled={isFormArchived}
                        >
                            Choose Image
                        </Button>

                        {previewImageUrl &&
                            <Box py={1} maxWidth="100%" display="flex" justifyContent="center">
                                <Box component="img" src={previewImageUrl} sx={{
                                    maxWidth: '100%',
                                    height: 'auto'
                                }}/>
                            </Box>
                        }

                        <RichEditor
                            value={description}
                            onChange={onChange}
                            index={index}
                            isEditMode={isEditMode}
                            isFormLive={isFormArchived}
                            accessLevel={accessLevel}
                            currentDescription={currentValue}
                            setCurrentDescription={handleDescriptionChange}
                            audience={audience}
                            recipientType={recipientType}
                        />

                        <AdmicityDialog
                            handleClose={handleDialogClose}
                            title="Add documents"
                            open={openAddDocumentsDialog}
                            actions={[
                                {
                                    label: 'Cancel',
                                    onClick: handleDialogClose
                                },
                                {
                                    label: 'Upload',
                                    onClick: handleFilesUpload,
                                    disabled: (isUploading || selectedFiles.size === 0) || isUploadSizeExceedsLimit,
                                    startIcon: isUploading ? <CircularProgress color="inherit" size={16}/> : undefined
                                }
                            ]}
                            maxWidth="md"
                        >
                            <Uploader
                                totalUploadSizeInMb={5}
                                onFilesSelect={handleFilesSelect}
                                onFileDelete={handleFileDeleteFromUploadContext}
                                onUploadLimitExceed={setIsUploadSizeExceedsLimit}
                                value={selectedFiles}
                                readonly={isUploading}
                                singleFile
                                fileTypes={{
                                    'image/jpeg': [],
                                    'image/png': [],
                                }}
                                allowedFileTypesLabel="Allowed file types: JPEG, PNG"
                            />
                        </AdmicityDialog>
                    </>
                )
                : (
                    <>
                        <RichEditorResultText
                            id={id}
                            description={description}
                            placeholderValues={placeholderValues}
                            readonly={readonly}
                            enableValidation={false}
                            audience={audience}
                            recipientType={recipientType}
                            submitMode={submitMode}
                        />
                        {previewImageUrl &&
                            <Box py={1} key={`file-${previewImageUrl}`} maxWidth="100%">
                                <Box component="img" src={previewImageUrl} sx={{
                                    maxWidth: '100%',
                                    height: 'auto'
                                }}/>
                            </Box>
                        }
                    </>
                )
            }
        </Box>
    );
};

Image.propTypes = {
    id: PropTypes.string,
    value: PropTypes.string,
    index: PropTypes.number,
    description: PropTypes.string,
    onChange: PropTypes.func,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    attachments: PropTypes.array,
    readonly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    submitMode: PropTypes.bool
};

export default Image;
